import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown, Check, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";

interface FeedbackButtonsProps {
  exampleId: string;
  originalText: string;
  ampelResult: string;
  aiHeadline: string;
  aiExplanation: string;
  kategorie?: string;
  onFeedbackSubmitted?: () => void;
}

export function FeedbackButtons({
  exampleId,
  originalText,
  ampelResult,
  aiHeadline,
  aiExplanation,
  kategorie,
  onFeedbackSubmitted
}: FeedbackButtonsProps) {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState<"correct" | "incorrect" | null>(null);
  const [loading, setLoading] = useState(false);

  const handleFeedback = async (feedback: "correct" | "incorrect") => {
    if (submitted) return;
    
    setLoading(true);
    try {
      // First, try to insert or update the training example
      const { error } = await supabase
        .from("klartext_training_examples")
        .upsert({
          id: exampleId,
          original_text: originalText,
          ampel_result: ampelResult,
          ai_headline: aiHeadline,
          ai_explanation: aiExplanation,
          training_category: kategorie || null,
          user_feedback: feedback,
          user_feedback_at: new Date().toISOString(),
          input_length: originalText.length
        }, { onConflict: 'id' });

      if (error) {
        // If upsert fails, try insert only
        const { error: insertError } = await supabase
          .from("klartext_training_examples")
          .insert({
            id: exampleId,
            original_text: originalText,
            ampel_result: ampelResult,
            ai_headline: aiHeadline,
            ai_explanation: aiExplanation,
            training_category: kategorie || null,
            user_feedback: feedback,
            user_feedback_at: new Date().toISOString(),
            input_length: originalText.length
          });
        
        if (insertError) throw insertError;
      }

      setSubmitted(feedback);
      toast({
        title: "Danke für Ihr Feedback!",
        description: feedback === "correct" 
          ? "Das hilft uns, besser zu werden." 
          : "Wir werden den Fall überprüfen.",
      });
      onFeedbackSubmitted?.();
    } catch (err) {
      console.error("Feedback error:", err);
      toast({
        title: "Feedback konnte nicht gespeichert werden",
        description: "Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-4 py-2 rounded-lg">
        <Check className="w-4 h-4" />
        <span>Danke für Ihr Feedback!</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <span className="text-sm text-slate-600">War diese Analyse hilfreich?</span>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleFeedback("correct")}
          disabled={loading}
          className="gap-2 hover:bg-green-50 hover:border-green-300 hover:text-green-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
          Ja, richtig
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleFeedback("incorrect")}
          disabled={loading}
          className="gap-2 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
          Nein, falsch
        </Button>
      </div>
    </div>
  );
}
